import { useAuthStore } from '../stores/auth';
import type { TokenResponse } from '../types';

export const API_BASE = ((import.meta.env.VITE_API_URL as string) || '/api').replace(/\/$/, '');
let authentication: Promise<boolean> | null = null;

// An ID can be displayed, but it is never proof of identity.
export function getTelegramUserId(): string | null {
  const user = useAuthStore.getState().user;
  return user?.telegram_id != null ? String(user.telegram_id) : null;
}

export function getAuthHeaders(): Record<string, string> {
  const { accessToken } = useAuthStore.getState();
  return accessToken ? { Authorization: 'Bearer ' + accessToken } : {};
}

function apiUrl(input: string): string {
  const base = new URL(API_BASE, window.location.origin);
  const target = input.startsWith('/api/') || input === '/api'
    ? new URL(API_BASE + input.slice(4), window.location.origin)
    : new URL(input, window.location.origin);
  if (target.origin !== base.origin || !(target.pathname === base.pathname || target.pathname.startsWith(base.pathname + '/'))) {
    throw new Error('Requests must target the configured API origin and path.');
  }
  target.searchParams.delete('tg_id');
  target.searchParams.delete('telegram_id');
  return target.href;
}

function setSession(data: TokenResponse): boolean {
  if (!data || typeof data.access_token !== 'string' || !data.access_token ||
      typeof data.refresh_token !== 'string' || !data.user || !Number.isSafeInteger(data.user.id)) return false;
  useAuthStore.setState({ accessToken: data.access_token, refreshToken: data.refresh_token,
    user: data.user, isAuthenticated: true });
  return true;
}

// Concurrent 401s share one exchange instead of overwriting newer tokens.
export function authenticate(): Promise<boolean> {
  if (authentication) return authentication;
  authentication = (async () => {
    const { refreshToken } = useAuthStore.getState();
    if (refreshToken) {
      const response = await fetch(apiUrl('/api/auth/refresh'), {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
      if (response.ok && setSession(await response.json())) return true;
      if (response.status >= 500) return false;
    }
    const initData = window.Telegram?.WebApp?.initData;
    if (!initData) return false;
    const response = await fetch(apiUrl('/api/auth/login'), {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ init_data: initData }),
    });
    return response.ok && setSession(await response.json());
  })().catch(() => false).finally(() => { authentication = null; });
  return authentication;
}

export async function authedFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const target = apiUrl(url);
  const send = () => {
    const headers = new Headers(options.headers);
    headers.delete('x-admin-secret');
    headers.delete('x-telegram-user-id');
    headers.delete('authorization');
    const token = useAuthStore.getState().accessToken;
    if (token) headers.set('Authorization', 'Bearer ' + token);
    return fetch(target, { ...options, headers });
  };
  const token = useAuthStore.getState().accessToken;
  let response = await send();
  if (response.status === 401 && !options.signal?.aborted) {
    const alreadyRenewed = useAuthStore.getState().accessToken !== token;
    if (alreadyRenewed || await authenticate()) response = await send();
  }
  return response;
}

export async function downloadAuthenticated(url: string, filename: string): Promise<void> {
  const response = await authedFetch(url);
  if (!response.ok) throw new Error('Download failed. Please try again.');
  const objectUrl = URL.createObjectURL(await response.blob());
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  link.click();
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}
